import React from "react";

import { List, ListItemText, Typography, ListItemButton, Divider, Box, ListItemIcon, Checkbox } from "@mui/material";

import Search from "../input/Search";
import ComponentLoader from "../misc/ComponentLoader";

import { isObjExistsInArray } from "../../helper-functions/shared/miscUtils";

/**
 * A selection component to choose one or more elements from a list.
 * Includes a search bar, and a list of elements alongside some text info about the currently selected item.
 *
 * Required props:
 * - callback: a function to call when the selection changes, receives the selected items as an argument.
 * - query: a function to query the items from the server, receives props and a data object with data and setData.
 * - getPrimarySearchText: a function which returns the field to search in the primary text (e.g. "name").
 * - getSecondarySearchText: a function which returns the field to search in the secondary text (e.g. "description").
 * - getPrimaryText: a function which returs the primary text of a list item, receives the item as an argument.
 * - getSecondaryText: a function which returs the secondary text of a list item, receives the item as an argument.
 *
 * Optional props:
 * - multiselect: select multiple items
 * - exclude: hide certain items from the list
 * - selected: initial selected items
 */
export default function CustomSelection(props) {
    // Required props
    const { callback, query } = props; // Because modals don't re-render if the props' state changes, use a callback.
    const { getPrimarySearchText, getSecondarySearchText } = props;
    const { getPrimaryText, getSecondaryText } = props;

    // Optional props
    const { multiselect = false, exclude = [], selected: initialSelected = [] } = props;

    const [data, setData] = React.useState(initialSelected ?? []);

    const [selected, setSelected] = React.useState([]);
    const [filtered, setFiltered] = React.useState([]);

    const [searchText, setSearchText] = React.useState("");
    const [prevSearchText, setPrevSearchText] = React.useState("");

    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setSelected(initialSelected ?? []);
        query(props, { data, setData });
    }, []);

    React.useEffect(() => {
        filterItems(searchText);
    }, [data]);

    React.useEffect(() => {
        if (loading && filtered.length === 0) return;

        callback(selected);
    }, [selected]);

    // After a timeout, show the "no entries" text.
    React.useEffect(() => {
        if (filtered.length !== 0) return;
        if (filtered.length === 0) setLoading(true);

        const loop = setTimeout(() => setLoading(false), 1500);
        return () => clearTimeout(loop);
    }, [filtered]);

    /**
     * Set the filtered rows according to the search text.
     * Query items from the server with this search param.
     */
    const filterItems = (searchText) => {
        const searchWords = searchText
            .toLowerCase()
            .split(",")
            .map((word) => word.trim());

        handleQuery(searchWords);

        const availableItems = data.filter((el) => !exclude.includes(el.id));

        const filteredItems = availableItems.filter((el) => {
            const aField = getPrimarySearchText(el)?.toLowerCase() ?? "";
            const bField = getSecondarySearchText(el)?.toLowerCase() ?? "";

            const aFound = searchWords.some((word) => aField?.includes(word));
            const bFound = searchWords.some((word) => bField?.includes(word));

            return aFound || bFound;
        });

        const sorted = getSortedFilteredItems(filteredItems);

        setFiltered(sorted);
    };

    /**
     * Sort the filtered items alphabetically.
     */
    const getSortedFilteredItems = (filteredItems) => {
        const sortedFilteredItems = filteredItems.sort((a, b) => {
            const aField = getPrimarySearchText(a)?.toLowerCase() ?? "";
            const bField = getPrimarySearchText(b)?.toLowerCase() ?? "";

            if (aField < bField) return -1;
            if (aField > bField) return 1;
            return 0;
        });

        return sortedFilteredItems;
    };

    /**
     * Query the authors from the server with the search parameter.
     * Doesn't query if the search text is empty or the search text has not changed.
     */
    const handleQuery = (searchWords) => {
        if ((searchWords && searchWords.length === 0) || searchWords.includes("")) return;
        if (searchText === prevSearchText) return; // Fix for infinite query loop.

        setPrevSearchText(searchText);
        query(props, { data, setData }, { filter: { items: [], quickFilterValues: searchWords }, pageSize: 20 });
    };

    /**
     * Executed when clicking on a list element.
     * Handles both single and multi-select.
     */
    const handleSelectionChange = (el) => {
        const isSelected = isObjExistsInArray(selected, el);

        if (multiselect) {
            setSelected((prev) => {
                if (isSelected) {
                    return prev.filter((p) => p.id !== el.id);
                } else {
                    return [...prev, el];
                }
            });
        } else {
            setSelected(isSelected ? [] : [el]);
        }
    };

    /**
     *  A list element with a checkbox.
     */
    const listElement = (el, i) => {
        const isSelected = isObjExistsInArray(selected, el);

        return (
            <ListItemButton key={i} selected={isSelected} onClick={() => handleSelectionChange(el)} dense>
                <ListItemIcon>
                    <Checkbox edge="start" checked={isSelected} onChange={() => handleSelectionChange(el)} disableRipple />
                </ListItemIcon>

                <ListItemText
                    primary={getPrimaryText(el)}
                    secondary={getSecondaryText(el)}
                    primaryTypographyProps={{ fontSize: "85%" }}
                    secondaryTypographyProps={{ fontSize: "75%" }}
                />
            </ListItemButton>
        );
    };

    /**
     *  Return info about the currently selected element(s).
     */
    const getCurrentSelectionInfo = () => {
        if (selected.length === 0) {
            return `Currently selected: N/A
            Name: N/A`;
        }

        return `Currently selected (${selected.length}):
        ${selected.map((el) => getPrimaryText(el)).join(", ")}`;
    };

    return (
        <Box ml={0.5} mr={0.5}>
            <Search
                {...props}
                callback={filterItems}
                searchText={searchText}
                setSearchText={setSearchText}
                fullWidth
                tooltip={`Separate keywords by commas (,)\nShows rows where at least one of the keywords are present.`}
            />

            {/* Scrollable list of selection elements */}
            <List className="hidden-scrollbar" sx={{ height: "175px" }}>
                <ComponentLoader isVisible={filtered.length === 0} isEmpty={false} height="85px" width="15%" />

                {!loading && filtered.length === 0 ? <ListItemText sx={{ textAlign: "center" }} primary={`No results found!`} /> : filtered.map((el, i) => listElement(el, i))}

                {filtered.length > 0 && <Typography className="italic sm-font sm-margin">Reached the end of the list - use the search field above to query more.</Typography>}
            </List>

            <Divider className="sm-margin" />

            {/* Text info about the currently selected element */}
            <Typography className="pre-line" variant="body2" color="textSecondary">
                {getCurrentSelectionInfo()}
            </Typography>
        </Box>
    );
}
